import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Input,
  Signal,
} from '@angular/core';
import { AlertLevelNames } from '@app/core/entity/alert.level';
import { BadgeProps, MiniBadgeProps } from '@bl/components';
import { TableService } from '../table/services/table.service';

const WIDE_ELEMENT_THRESHOLD = 80;

@Component({
  selector: 'app-status-icon',
  template: `
    @if (status) {
      @if (isWideElement()) {
        <div class="flex gap-1">
          @if (statusIcon.name === 'calendar') {
            <bl-frontend-tooltip [content]="tooltipText">
              <app-calendar-status-icon></app-calendar-status-icon>
            </bl-frontend-tooltip>
          } @else {
            <bl-frontend-tooltip [content]="tooltipText">
              <bl-frontend-badge
                [variant]="statusVariant?.variant"
                [ariaLabel]="title"
                [emphasis]="statusVariant?.emphasis"
                [leadingIcon]="statusIcon"></bl-frontend-badge>
            </bl-frontend-tooltip>
          }
          <div class="truncate">{{ title }}</div>
        </div>
      } @else {
        @if (statusIcon.name === 'calendar') {
          <bl-frontend-tooltip [content]="tooltipText">
            <app-calendar-status-icon></app-calendar-status-icon>
          </bl-frontend-tooltip>
        } @else {
          <bl-frontend-tooltip [content]="tooltipText">
            <bl-frontend-badge
              [variant]="statusVariant?.variant"
              [ariaLabel]="title"
              [emphasis]="statusVariant?.emphasis"
              [leadingIcon]="statusIcon"></bl-frontend-badge>
          </bl-frontend-tooltip>
        }
      }
    } @else {
      <bl-frontend-icon name="minus" [ariaLabel]="title"></bl-frontend-icon>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'min-w-0' },
})
export class StatusIconComponent implements AfterViewInit {
  @Input() status: string;
  @Input() color: string;
  @Input() title: string;
  @Input() tooltip: string;

  private elementRef: ElementRef;
  private tableService: TableService;
  protected isWideElement: Signal<boolean>;

  constructor() {
    this.tableService = inject(TableService);
    this.elementRef = inject(ElementRef);

    this.isWideElement = computed(() => {
      this.tableService.onColumnResized();
      const elementOffsetWidth = this.elementRef.nativeElement.parentElement.offsetWidth;
      return elementOffsetWidth > WIDE_ELEMENT_THRESHOLD;
    });
  }

  ngAfterViewInit(): void {
    // Initial check for element width
    this.isWideElement();
  }

  get tooltipText() {
    if (this.isWideElement()) {
      return this.tooltip;
    }
    if (this.tooltip) {
      return `${this.tooltip}${this.title ? ' : ' + this.title : ''}`;
    }
    return this.title || '';
  }

  // eslint-disable-next-line complexity
  get statusVariant(): { variant: MiniBadgeProps['variant']; emphasis: MiniBadgeProps['emphasis'] } | null {
    switch (this.status) {
      case AlertLevelNames.WARNING:
        return { variant: 'warning', emphasis: 'subtle' };
      case AlertLevelNames.REMINDER:
        return { variant: 'warning', emphasis: 'subtle' };
      case AlertLevelNames.OVERDUE:
        return { variant: 'danger', emphasis: 'subtle' };
      case AlertLevelNames.COMPLETED:
        return { variant: 'success', emphasis: 'subtle' };
      case AlertLevelNames.STARTED:
        return { variant: 'info', emphasis: 'subtle' };
      case AlertLevelNames.INACTIVE:
        return { variant: 'neutral', emphasis: 'subtle' };
      default:
        return null;
    }
  }

  // eslint-disable-next-line complexity
  get statusIcon(): BadgeProps['leadingIcon'] | null {
    switch (this.status) {
      case AlertLevelNames.INACTIVE:
        return { name: 'calendar' };
      case AlertLevelNames.STARTED:
        return { name: 'play' };
      case AlertLevelNames.REMINDER:
        return { name: 'bell' };
      case AlertLevelNames.OVERDUE:
        return { name: 'exclamation-circle' };
      // return { name: 'exclamation-circle', variant: 'solid' };
      case AlertLevelNames.WARNING:
        return { name: 'exclamation-triangle' };
      case AlertLevelNames.COMPLETED:
        return { name: 'check' };
      default:
        return null;
    }
  }
}
